import React, { FC, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router';
import AttendantRoomItemHeader from '@hkm/components/Attendant/RoomList/Body/AttendantRoomHeader/AttendantRoomItemHeader';
import { AttendantSheetState } from '@hkm/components/Attendant/shared/domain/interfaces/assignmentData';
import { selectAssignedSheetState } from '@hkm/components/Attendant/shared/domain/selectors';
import { getGuestServiceStatus } from '@hkm/components/Attendant/shared/utils/getGuestServiceStatus';
import {
  selectBusinessDate,
  selectEffectiveValues,
  selectPropertyDateFormats
} from '@hkm/components/Menu/PropertySelector/domain/selectors';
import EtaEtdGuaranteedBadge from '@hkm/components/shared/EtaEtdGuaranteedBadge/EtaEtdGuaranteedBadge';
import GuestServiceBadge from '@hkm/components/shared/GuestService/GuestServiceBadge';
import MaintenanceStatusBadge from '@hkm/components/shared/MaintenanceStatusBadge/MaintenanceStatusBadge';
import ReservationMovementDashboardItem from '@hkm/components/shared/ReservationMovements/ReservationMovementDashboardItem';
import { RoomConditionsBadge } from '@hkm/components/shared/RoomConditionsBadge/RoomConditionsBadge';
import RoomStatusBadge from '@hkm/components/shared/RoomStatusBadge/RoomStatusBadge';
import APP_ROUTES from '@hkm/constants/routing.constants';
import { GuestServiceStatusType } from '@hkm/shared/enum/guestServiceStatusType';
import { useGuestServiceMap } from '@hkm/shared/hooks/useGuestServiceMap';
import { getCurrentMainReservation } from '@hkm/shared/reservedKind/getCurrentMainReservation';
import { AttendantRoom } from '@hkm/types/attendant/models/AttendantRoom';
import { dayjs } from '@hkm/utils/dayjs-extended';

import {
  AttendantTaskProgressType,
  FrontDeskStatus,
  ReservationStatus,
  RoomMaintenanceStatus,
  RoomStatus
} from '@ac/library-api';
import { AcFlex } from '@ac/mobile-components/dist/components/flex';
import {
  AcFormElement,
  AcFormGroup,
  AcFormRow
} from '@ac/mobile-components/dist/components/form-element';
import { AcIcon } from '@ac/mobile-components/dist/components/icon';
import { AcText } from '@ac/mobile-components/dist/components/text';
import { AcTile } from '@ac/mobile-components/dist/components/tile';
import {
  AlignItems,
  Color,
  Icon,
  IconSize,
  TextSize
} from '@ac/mobile-components/dist/enums';
import { formatTestSelector } from '@ac/mobile-components/dist/utils';

import './AttendantRoomItem.css';

interface AttendantRoomItemProps {
  room: AttendantRoom;
  taskType: AttendantTaskProgressType;
}

const AttendantRoomItem: FC<AttendantRoomItemProps> = (
  props: AttendantRoomItemProps
) => {
  const { t } = useTranslation();
  const prefix = 'attendant-room-item';
  const guestServiceMap = useGuestServiceMap();
  const guestService = getGuestServiceStatus(props.room);
  const isGreenServiceEnabled = useSelector(selectEffectiveValues)!
    .greenService;
  const businessDate = useSelector(selectBusinessDate);
  const { shortDate: shortDateFormat } = useSelector(selectPropertyDateFormats);
  const mainReservation = useMemo(() => {
    return getCurrentMainReservation(
      props.room.reservations.map(reservation => ({
        ...reservation,
        status: reservation.status?.code as ReservationStatus
      })),
      businessDate
    );
  }, [props.room.reservations, businessDate]);

  const showGreenService = isGreenServiceEnabled && props.room.greenService;
  const showLinenChange = props.room.linenChange;
  const showEtaEtdGuaranteed = !!(
    mainReservation?.status === ReservationStatus.IH &&
    mainReservation?.isTimeGuaranteed
  );

  const arrivalDate = props.room.currentMainReservation?.arrivalDate
    ? dayjs(props.room.currentMainReservation?.arrivalDate).format(
        shortDateFormat
      )
    : undefined;
  const departureDate = props.room.currentMainReservation?.arrivalDate
    ? dayjs(props.room.currentMainReservation?.departureDate).format(
        shortDateFormat
      )
    : undefined;

  const hasServiceDeferred =
    guestServiceMap.get(GuestServiceStatusType.ServiceDeferred) &&
    props.room.serviceDeferred;

  const hasRoomConditions = !!props.room.roomConditions?.length;
  const navigate = useNavigate();
  const assignedSheetState: AttendantSheetState = useSelector(
    selectAssignedSheetState
  );

  const redirectToDetails = useCallback(() => {
    navigate(
      generatePath(APP_ROUTES.ATTENDANT_ASSIGNMENTS.ROOM_DETAILS, {
        sheetId: assignedSheetState.data!.id,
        roomId: props.room.id
      })
    );
  }, [props.room]);

  const { isInPendingRoomQueue } = props.room;

  return (
    <>
      <AcTile className={prefix}>
        <div onClick={redirectToDetails}>
          <AttendantRoomItemHeader
            isInPendingRoomQueue={isInPendingRoomQueue}
            room={props.room}
            taskType={props.taskType}
          >
            {props.room.roomNumber}
          </AttendantRoomItemHeader>

          <div
            className={`${prefix}-content ac-spacing-top-md ${
              isInPendingRoomQueue ? 'is-in-queue' : ''
            }`}
          >
            <AcFormGroup className="ac-spacing-top-md">
              {props.room.frontdeskStatus?.code ===
                FrontDeskStatus.Occupied && (
                <AcFormRow>
                  <AcFormElement
                    label={t('ROOM_DETAILS.ARRIVAL_DATE')}
                    testSelector={formatTestSelector(prefix, 'arrivalDate')}
                  >
                    {arrivalDate}
                  </AcFormElement>
                  <AcFormElement
                    label={t('ROOM_DETAILS.DEPARTURE_DATE')}
                    testSelector={formatTestSelector(prefix, 'departureDate')}
                  >
                    {departureDate}
                  </AcFormElement>
                </AcFormRow>
              )}

              <AcFormRow>
                <AcFormElement
                  label={t('GLOBAL.ROOM_TYPE.LONG')}
                  testSelector={formatTestSelector(prefix, 'roomType')}
                >
                  {props.room.roomType?.code}
                </AcFormElement>
                <AcFormElement
                  label={t('GLOBAL.ROOM_STATUS.LONG')}
                  testSelector={formatTestSelector(prefix, 'roomStatus')}
                >
                  <RoomStatusBadge
                    status={props.room.roomStatus?.code as RoomStatus}
                    showLabel={true}
                  />
                </AcFormElement>
              </AcFormRow>

              <AcFormRow>
                <AcFormElement
                  label={t('GLOBAL.CLEARING_TYPE.TITLE')}
                  testSelector={formatTestSelector(prefix, 'cleaningType')}
                >
                  {props.room.serviceType
                    ? props.room.serviceType.description
                    : t(`GLOBAL.NONE`)}
                </AcFormElement>
                <AcFormElement
                  label={t('GLOBAL.FRONT_DESK_STATUS.LONG')}
                  testSelector={formatTestSelector(prefix, 'frontDesk')}
                >
                  {props.room.frontdeskStatus!.description}
                </AcFormElement>
              </AcFormRow>

              {props.room.currentMaintenance && (
                <AcFormRow>
                  <AcFormElement
                    label={t('GLOBAL.MAINTENANCE_STATUS.TITLE')}
                    testSelector={formatTestSelector(prefix, 'cleaningType')}
                  >
                    <MaintenanceStatusBadge
                      status={
                        props.room.currentMaintenance!.status!
                          .code as RoomMaintenanceStatus
                      }
                      testSelector={formatTestSelector(prefix, 'maintenance')}
                    />
                  </AcFormElement>
                </AcFormRow>
              )}

              <ReservationMovementDashboardItem
                room={props.room}
                testSelector={formatTestSelector(
                  prefix,
                  'reservation-movement'
                )}
              />

              {(guestService || showGreenService || showLinenChange) && (
                <AcFormRow>
                  {guestService && (
                    <AcFormElement
                      label={t('GLOBAL.GUEST_SERVICE_STATUS.TITLE')}
                      testSelector={formatTestSelector(prefix, 'guestService')}
                    >
                      {t(`GLOBAL.GUEST_SERVICE_STATUS.VALUES.${guestService}`)}
                    </AcFormElement>
                  )}

                  {showGreenService && (
                    <AcFlex alignItems={AlignItems.center}>
                      <AcIcon
                        icon={Icon.GreenService}
                        size={IconSize.Medium}
                        className="ac-spacing-right-md"
                      />
                      <AcText
                        color={Color.Black}
                        size={TextSize.Main2}
                        testSelector={`${prefix}-greenService`}
                      >
                        {t('GLOBAL.GREEN_SERVICE.TITLE')}
                      </AcText>
                    </AcFlex>
                  )}

                  {showLinenChange && (
                    <AcFlex alignItems={AlignItems.center}>
                      <AcIcon
                        icon={Icon.LinenChange}
                        size={IconSize.Medium}
                        className="ac-spacing-right-md"
                      />
                      <AcText
                        color={Color.Black}
                        size={TextSize.Main2}
                        testSelector={`${prefix}-linenChange`}
                      >
                        {t('GLOBAL.LINEN_CHANGE')}
                      </AcText>
                    </AcFlex>
                  )}
                </AcFormRow>
              )}

              {hasServiceDeferred && (
                <AcFlex alignItems={AlignItems.center}>
                  <GuestServiceBadge
                    serviceDeferredUntil={props.room.serviceDeferredUntil}
                  />
                </AcFlex>
              )}

              {hasRoomConditions && (
                <RoomConditionsBadge
                  roomConditions={props.room.roomConditions}
                />
              )}

              {showEtaEtdGuaranteed && (
                <AcFlex>
                  <EtaEtdGuaranteedBadge />
                </AcFlex>
              )}
            </AcFormGroup>
          </div>
        </div>
      </AcTile>
    </>
  );
};

export default memo(AttendantRoomItem);



// WEBPACK FOOTER //
// ./src/components/Attendant/RoomList/Body/AttendantRoomItem/AttendantRoomItem.tsx